import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, useToast } from '@vd/ui';
import { Download } from 'lucide-react';
import { api } from '../../../lib/api';
import { requestRaw } from '../../../lib/apiClient';
import { useAuth } from '../../../features/auth/AuthProvider';
import { billingQueryKey, fetchSubscription } from '../../../features/billing/useBillingSummary';
import styles from '../Settings.module.css';
import { dayAfter, midnightIn } from '../../../lib/time';
import { When } from '../../../lib/When';

/**
 * The export's query for a chosen range of days. The days are the owner's
 * own (the date inputs are local), so each is sent as the exact instant it
 * starts where they are: `from` the first day's midnight, `to` the midnight
 * after the last day, which the server leaves out (review round 1: sending
 * bare dates read them as UTC days, a few hours off at both ends).
 */
export function exportRangeQuery(from: string, to: string): string {
  const params = new URLSearchParams();
  const start = from ? midnightIn(from) : null;
  // The start of the next day, found in the person's zone too: adding a day
  // to a midnight in the browser's zone was an hour off across a change of
  // clocks (Codex, TOADO-904 round 1).
  const end = to ? midnightIn(dayAfter(to)) : null;
  if (start) params.set('from', start.toISOString());
  if (end) params.set('to', end.toISOString());
  const query = params.toString();
  return query ? `?${query}` : '';
}


function describeResource(action: string, resourceType: string, resourceId: string, metadata?: Record<string, unknown>): string {
  if (metadata?.email) return String(metadata.email);
  if (metadata?.title) return String(metadata.title);
  if (metadata?.name) return String(metadata.name);
  if (action.startsWith('ticket.')) return resourceId;
  if (action.startsWith('project.')) return resourceId;
  return `${resourceType} ${resourceId}`;
}

const ACTION_LABELS: Record<string, string> = {
  'ticket.created': 'Created ticket',
  'ticket.updated': 'Updated ticket',
  'ticket.moved': 'Moved ticket',
  'ticket.assigned': 'Assigned ticket',
  'ticket.unassigned': 'Unassigned ticket',
  'ticket.assignment_suggestion_dismissed': 'Dismissed an assignee suggestion',
  'ticket.archived': 'Archived ticket',
  'ticket.restored': 'Restored ticket',
  'ticket.deleted': 'Deleted ticket',
  'comment.created': 'Commented on ticket',
  'project.created': 'Created project',
  'project.updated': 'Updated project',
  'project.deleted': 'Deleted project',
  'column.updated': 'Updated columns',
  'member.invited': 'Invited member',
  'member.invite_resent': 'Resent invite',
  'member.invite_revoked': 'Revoked invite',
  'member.joined': 'Joined company',
  'member.role_changed': 'Changed member role',
  'member.removed': 'Removed member',
  'company.updated': 'Updated company',
  'company.deleted': 'Deleted company',
};

export function AuditLogTab() {
  const { currentCompanyId } = useAuth();
  const toast = useToast();

  const { data: members = [] } = useQuery({
    queryKey: ['members', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });
  const actorName = (id: string) => {
    const m = members.find((x) => x.user.id === id);
    // When a member is removed, their audit history persists but the
    // members list no longer has their user record. Showing the raw
    // user id (e.g. "u-aB3xY9pQ") looks like a debugging artifact and
    // doesn't help the viewer reconstruct what happened. "Former
    // member" reads as intentional; the actor id is still in the
    // exported CSV when fine-grained tracking is needed.
    return m?.user.name ?? 'Former member';
  };

  const { data } = useQuery({
    queryKey: ['audit', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listAuditEntries(currentCompanyId, { limit: 100 }) : Promise.resolve({ items: [] }),
    enabled: !!currentCompanyId,
  });

  const entries = data?.items ?? [];

  // The CSV export is owner-only on the API side and is an explicit,
  // deliberate download (not a passive view). It still includes actor
  // emails so the file is useful for compliance / incident response
  // workflows that need to reconstruct who-did-what at a specific time.
  // The in-app table is intentionally email-free because passive views
  // are easier to leak via screenshots; the CSV is opt-in by an admin
  // who already has full member visibility elsewhere.
  //
  // Fetched through the API client and saved from here, not opened in a new
  // window: a window's request cannot name the account this page was built
  // for, so a page left open on one owner could download another's log once
  // they had signed in from another tab (review of 0.2.0, 3 Oct).
  //
  // Business and Enterprise choose the dates the file covers, and their file
  // carries each entry's details (TOADO-662). Other plans get the latest
  // 10,000 entries, as before.
  // The plan decides what the export can do, so it is known before the button
  // works: an export started before it would leave out the chosen dates.
  const billing = useQuery({
    queryKey: billingQueryKey(currentCompanyId ?? undefined),
    queryFn: () => fetchSubscription(currentCompanyId!),
    enabled: !!currentCompanyId,
    staleTime: 60 * 1000,
  });
  const plan = billing.data?.plan;
  const fullExport = plan === 'business' || plan === 'enterprise';
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const exportCsv = async () => {
    if (!currentCompanyId) return;
    try {
      const range = fullExport ? exportRangeQuery(from, to) : '';
      const res = await requestRaw(`/companies/${currentCompanyId}/audit/export${range}`);
      if (res.headers.get('x-export-truncated') === 'true') {
        toast.push({
          variant: 'warning',
          title: `The file holds the newest ${fullExport ? '100,000' : '10,000'} entries`,
          description: fullExport ? 'Choose a shorter range for the ones before them.' : 'Older entries are not in it.',
        });
      }
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-${currentCompanyId}.csv`;
      link.click();
      // Let go of the file once the download has surely started: some
      // browsers abandon a download whose address is revoked at once.
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Export failed', description: (error as Error).message });
    }
  };

  return (
    <>
      <div className={styles.rowSpread}>
        <div>
          <h1 className={styles.h1}>Audit log</h1>
          <p className={styles.sub}>Admin-visible actions across the company, retained 1 year.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {fullExport && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                From
                <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} aria-label="Export from" />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                To
                <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} aria-label="Export to" />
              </label>
            </>
          )}
          <Button variant="secondary" leftIcon={<Download size={14} />} disabled={billing.isPending} onClick={() => { void exportCsv(); }}>Export CSV</Button>
        </div>
      </div>
      <section className={styles.section}>
        <table className={styles.table}>
          <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Resource</th></tr></thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={4} style={{ color: 'var(--text-tertiary)', padding: 24, textAlign: 'center' }}>
                No audit entries yet. Mutations (create, update, delete, archive, assign, invite…) will show up here.
              </td></tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id}>
                  <td style={{ color: 'var(--text-secondary)', verticalAlign: 'top' }}><When at={e.occurredAt} /></td>
                  <td style={{ verticalAlign: 'top' }}>
                    <div>
                      {actorName(e.actorId)}
                      {e.actorType === 'mcp_client' && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--accent-mcp, var(--accent))' }}>via MCP</span>
                      )}
                    </div>
                  </td>
                  <td style={{ verticalAlign: 'top' }}>{ACTION_LABELS[e.action] ?? e.action}</td>
                  <td style={{
                    color: 'var(--text-secondary)',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    fontSize: 12,
                    // Wrap long resource text (error messages from
                    // ticket.created metadata can be 200+ chars) so it
                    // stays inside the cell instead of pushing the
                    // table wider than the page.
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    maxWidth: 0, // forces the cell to honor the table width and wrap
                    verticalAlign: 'top',
                  }}>
                    {describeResource(e.action, e.resourceType, e.resourceId, e.metadata)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </>
  );
}
